/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';

import * as client from 'DesignData/api/finder/Folders';
import getQuickFetchRequestPromise from 'DesignData/api/getQuickFetchRequestPromise';
import { FOLDER } from 'DesignData/constants/FolderAssetTypes';
import { generateCopyName, isHollowFolderId } from 'DesignData/helpers/finderHelpers';
import * as finderSelectors from 'DesignData/selectors/finder';
import { getPortalId, getUserId } from 'DesignData/selectors/auth';
import { createAsyncActions } from 'DesignData/lib/actions';
import { getIsLocked, getIsHubspotFolder } from 'DesignData/selectors/finder';
import { ROOT_FOLDER_ID } from 'DesignData/constants/Folders';
import { copyToClipboard } from 'DesignData/lib/copySnippets';
import { convertToAbsolute } from 'DesignData/lib/path';
import { EXTENSION as SERVERLESS_EXTENSION } from 'DesignData/lib/serverless/serverless';
import { fetchDefault } from 'DesignData/actions/finder/FileMapper';
import { FINDER_FOLDER_CONTENTS_BULK_DELETE, FINDER_FOLDER_CONTENTS_FETCH, FINDER_FOLDER_COPY_PATH, FINDER_FOLDER_CREATE, FINDER_FOLDER_DELETE, FINDER_FOLDER_DUPLICATE, FINDER_FOLDER_CONTENTS_MOVE, FINDER_FOLDER_PARENTS_FETCH, FINDER_FOLDER_RENAME, FINDER_FOLDER_TOGGLE_OPEN, FINDER_FOLDER_TOGGLE_LOCK, FINDER_FOLDERS_BULK_DELETE, FINDER_FOLDERS_COLLAPSE_ALL, FINDER_OPEN_FOLDERS_FETCH, FINDER_OPEN_FOLDERS_SAVE, FINDER_ROOT_FOLDER_CONTENTS_FETCH, FINDER_FOLDER_OPEN_DEEPLY_NESTED } from 'DesignData/actions/ActionTypes';

const processFolderIds = (getState, folderIds) => {
  folderIds = Array.isArray(folderIds) ? folderIds : [folderIds];
  const hubspotFolderIds = [];
  const ids = Array.from(new Set(folderIds));
  folderIds = ids.filter(folderId => {
    if (!folderId || folderId === 'undefined') return false;

    if (getIsHubspotFolder(getState(), {
      folderId
    })) {
      hubspotFolderIds.push(folderId);
      return false;
    }

    return true;
  });
  return {
    folderIds,
    hubspotFolderIds
  };
};
/**
 * GET designmanager/v1/folders/:folderId/contents
 * @param  {Number} folderId ID of folder to fetch contents of.
 */


export function fetchContents(folderId, actionType = FINDER_FOLDER_CONTENTS_FETCH) {
  const {
    request,
    receive,
    error
  } = fetchContents;
  return (dispatch, getState) => {
    const {
      folderIds,
      hubspotFolderIds
    } = processFolderIds(getState, folderId);
    const rootFolderId = finderSelectors.getRootFolderId(getState()); // Without this condition, calling fetchContents on the root folder
    // causes the store to be corrupted, e.g., @hubspot will disappear.

    if (actionType === FINDER_FOLDER_CONTENTS_FETCH && (folderId === ROOT_FOLDER_ID || folderId === rootFolderId)) {
      actionType = FINDER_ROOT_FOLDER_CONTENTS_FETCH;
    }

    if (hubspotFolderIds.length) {
      dispatch(fetchDefault());
    }

    folderId = folderIds;
    if (!folderId.length) return null;
    if (folderId.length === 1) folderId = folderId[0];
    dispatch(request(folderId, actionType));

    const successHandler = ({
      folder,
      files,
      subfolders,
      readOnly,
      folderMetadata
    }) => {
      dispatch(receive({
        folder,
        files,
        readOnly,
        subfolders,
        folderMetadata
      }, actionType));
    };

    const errorHandler = __error => {
      dispatch(error(folderId, __error, actionType));
    };

    if (folderId === ROOT_FOLDER_ID || folderId === rootFolderId) {
      const quickFetchPromise = getQuickFetchRequestPromise('root-folder-contents', () => client.fetchContents(folderId));

      if (quickFetchPromise) {
        quickFetchPromise.then(successHandler).catch(errorHandler);
        return undefined;
      }
    }

    return client.fetchContents(folderId).then(successHandler).catch(errorHandler);
  };
}

fetchContents.request = function fetchContentsRequest(folderId, type = FINDER_FOLDER_CONTENTS_FETCH) {
  return {
    type,
    folderId
  };
};

fetchContents.receive = function fetchContentsReceive({
  folder,
  files,
  subfolders,
  folderMetadata,
  readOnly
}, type = FINDER_FOLDER_CONTENTS_FETCH) {
  return {
    type,
    folderId: folder.id,
    contents: {
      folder,
      readOnly,
      folderMetadata,
      files,
      subfolders
    }
  };
};

fetchContents.error = function fetchContentsError(folderId, error, type = FINDER_FOLDER_CONTENTS_FETCH) {
  return {
    type,
    folderId,
    error
  };
};
/**
 * GET designmanager/v1/folders/root/contents
 */


export function fetchRootFolderContents() {
  return fetchContents(ROOT_FOLDER_ID, FINDER_ROOT_FOLDER_CONTENTS_FETCH);
}
/**
 * Fetch open folders from localStorage, then fetch contents
 * GET designmanager/v1/folders/contents?ids=&ids=
 */

export function fetchOpenFolders() {
  const {
    request,
    receive,
    error
  } = fetchOpenFolders;
  return (dispatch, getState) => {
    const state = getState();
    const userId = getUserId(state);
    const portalId = getPortalId(state);

    const __folderIds = client.fetchOpenFolders(userId, portalId);

    const {
      folderIds,
      hubspotFolderIds
    } = processFolderIds(getState, __folderIds);

    if (hubspotFolderIds.length) {
      dispatch(fetchDefault());
    }

    if (!folderIds.length) return;
    dispatch(request(folderIds));
    client.fetchContents(folderIds).then(contents => {
      dispatch(receive(folderIds, contents, hubspotFolderIds));
    }).catch(err => dispatch(error(folderIds, err)));
  };
}

fetchOpenFolders.request = folderIds => {
  return {
    type: FINDER_OPEN_FOLDERS_FETCH,
    folderIds
  };
};

fetchOpenFolders.receive = (folderIds, contents, hubspotFolderIds) => {
  return {
    type: FINDER_OPEN_FOLDERS_FETCH,
    folderIds,
    contents,
    hubspotFolderIds
  };
};

fetchOpenFolders.error = (folderIds, error) => {
  return {
    type: FINDER_OPEN_FOLDERS_FETCH,
    folderIds,
    error
  };
};
/**
 * GET designmanager/v1/folders/:folderId/parents
 */


export function fetchParents(folderId) {
  const {
    request,
    receive,
    error
  } = fetchParents;
  return dispatch => {
    dispatch(request(folderId));
    return client.fetchParents(folderId).then(filesWithParents => {
      dispatch(receive(folderId, filesWithParents));
    }).catch(__error => {
      dispatch(error(folderId, __error));
    });
  };
}

fetchParents.request = folderId => {
  return {
    type: FINDER_FOLDER_PARENTS_FETCH,
    folderId
  };
};

fetchParents.receive = (folderId, files) => {
  return {
    type: FINDER_FOLDER_PARENTS_FETCH,
    folderId,
    files
  };
};

fetchParents.error = (folderId, error) => {
  return {
    type: FINDER_FOLDER_PARENTS_FETCH,
    folderId,
    error
  };
};
/**
 * Save open folders to localStorage
 */


export function saveOpenFolders() {
  return (dispatch, getState) => {
    const {
      request,
      receive,
      error
    } = saveOpenFolders;
    const state = getState();
    const openFolderIds = finderSelectors.getOpenFolderIds(state);
    const userId = getUserId(state);
    const portalId = getPortalId(state);
    dispatch(request());
    return client.saveOpenFolders(userId, portalId, openFolderIds).then(result => dispatch(receive(result))).catch(err => dispatch(error(err)));
  };
}
Object.assign(saveOpenFolders, createAsyncActions('result', FINDER_OPEN_FOLDERS_SAVE));
/**
 * Toggle folder open state
 * @param  {Number} folderId  ID of folder to toggle open state.
 * @param  {Boolean} open     Flag indicating open state.
 */

export function toggleOpenFolder(folderId, open) {
  const {
    request
  } = toggleOpenFolder;
  return (dispatch, getState) => {
    if (!folderId) return;
    dispatch(request(folderId, open));

    if (!open) {
      const state = getState();
      const openFolders = finderSelectors.getOpenFolders(state);
      const closedFolders = {
        [folderId]: true
      };
      Object.keys(openFolders).forEach(__folderId => {
        const folder = openFolders[__folderId];
        const {
          id,
          parentFolderId
        } = folder;

        if (closedFolders[parentFolderId]) {
          closedFolders[id] = true;
          dispatch(request(id, false));
        }
      });
    }
  };
}

toggleOpenFolder.request = (folderId, open) => {
  return {
    type: FINDER_FOLDER_TOGGLE_OPEN,
    folderId,
    open
  };
};
/**
 * Collapse all open folders
 */


export function collapseAllFolders() {
  return {
    type: FINDER_FOLDERS_COLLAPSE_ALL
  };
}
export function openDeeplyNestedFolder(folderId) {
  const {
    request,
    receive,
    error
  } = openDeeplyNestedFolder;

  if (isHollowFolderId(folderId)) {
    return (dispatch, getState) => {
      if (getIsHubspotFolder(getState(), {
        folderId
      })) {
        dispatch(fetchDefault());
      }

      let path = folderId;

      if (folderId.startsWith('/')) {
        path = path.slice(1);
      }

      const segments = path.split('/');
      const parentIds = [];

      if (segments.length > 2) {
        segments.pop();
        segments.forEach((segment, index) => {
          if (index === 0) {
            parentIds.push(`/${segment}`);
          } else {
            parentIds.push(`${parentIds[index - 1]}/${segment}`);
          }
        });
      }

      dispatch(receive(folderId, parentIds));
    };
  }

  return dispatch => {
    dispatch(request(folderId));
    return client.fetchParentIds(folderId).then(parentFolderIds => {
      dispatch(receive(folderId, parentFolderIds));
    }).catch(__error => {
      dispatch(error(__error, folderId));
    });
  };
}

openDeeplyNestedFolder.request = folderId => {
  return {
    type: FINDER_FOLDER_OPEN_DEEPLY_NESTED,
    folderId
  };
};

openDeeplyNestedFolder.receive = (folderId, parentFolderIds) => {
  return {
    type: FINDER_FOLDER_OPEN_DEEPLY_NESTED,
    parentFolderIds,
    folderId
  };
};

openDeeplyNestedFolder.error = (error, folderId) => {
  return {
    type: FINDER_FOLDER_OPEN_DEEPLY_NESTED,
    folderId,
    error
  };
};
/**
 * POST designmanager/v1/folders
 * @param {String} name Name of folder to create
 * @param {Number} parentFolderId ID of parent folder to create new folder in
 */


export function createFolder(name, __parentFolderId) {
  const {
    request,
    receive,
    error
  } = createFolder;
  return (dispatch, getState) => {
    const state = getState();

    const parentFolderId = __parentFolderId || finderSelectors.getRootFolderId(state);

    dispatch(request(name, parentFolderId));
    return client.create({
      name,
      parentFolderId
    }).then(folder => {
      dispatch(receive(folder, parentFolderId));
      return {
        name,
        folder,
        parentFolderId
      };
    }).catch(__error => {
      dispatch(error(__error, name));
      return {
        error: __error,
        name,
        parentFolderId
      };
    });
  };
}

createFolder.request = function createFolderRequest(name, parentFolderId) {
  return {
    type: FINDER_FOLDER_CREATE,
    name,
    parentFolderId
  };
};

createFolder.receive = function createFolderReceive(folder, parentFolderId) {
  return {
    type: FINDER_FOLDER_CREATE,
    folder,
    parentFolderId
  };
};

createFolder.error = function createFolderError(error, name) {
  return {
    type: FINDER_FOLDER_CREATE,
    error,
    name
  };
};
/**
 * DELETE designmanager/v1/folders/:folderId
 */


export function deleteFolder(folderId, __parentFolderId) {
  const {
    request,
    receive,
    error
  } = deleteFolder;
  return (dispatch, getState) => {
    const state = getState();

    const parentFolderId = __parentFolderId || finderSelectors.getRootFolderId(state);

    const nestedFolderContents = finderSelectors.getNestedFolderContents(state, {
      folderId
    });
    dispatch(request(folderId));
    return client.del(folderId).then(() => {
      dispatch(receive(folderId, parentFolderId, nestedFolderContents));
    }).catch(__error => {
      dispatch(error(folderId, __error));
    });
  };
}

deleteFolder.request = folderId => {
  return {
    type: FINDER_FOLDER_DELETE,
    folderId
  };
};

deleteFolder.receive = (folderId, parentFolderId, contents) => {
  return {
    type: FINDER_FOLDER_DELETE,
    folderId,
    parentFolderId,
    contents,
    deleted: true
  };
};

deleteFolder.error = (folderId, error) => {
  return {
    type: FINDER_FOLDER_DELETE,
    folderId,
    error
  };
};
/**
 * PUT designmanager/v1/folders/:id/contents
 * @param {Object[]} contents - Array of assets to move.
 * @param {Number} contents[].id - ID of asset to move.
 * @param {FolderAssetType} contents[].type - Type of asset to move.
 * @param {Number} contents[].parentFolderId - ID of old parent folder.
 * @param {Number} folderId - ID of target folder.
 */


export function moveContents(contents, targetFolderId) {
  const {
    request,
    receive,
    error
  } = moveContents;
  return dispatch => {
    const requestData = contents.map(asset => {
      return {
        id: asset.id,
        type: asset.type || FOLDER
      };
    });
    dispatch(request(requestData, targetFolderId));
    client.addContents(targetFolderId, requestData).then(([targetFolderContents]) => {
      const fileIds = (targetFolderContents.files || []).map(el => el.id);
      const subfolders = (targetFolderContents.subfolders || []).map(el => el.folder);
      const subfolderIds = (subfolders || []).map(el => el.id);
      const results = contents.map(asset => {
        return Object.assign({}, asset, {
          cloned: asset.type === FOLDER ? !~subfolderIds.indexOf(asset.id) : !~fileIds.indexOf(asset.id)
        });
      });
      dispatch(receive(results, targetFolderId, targetFolderContents));
    }).catch(__error => {
      dispatch(error(__error, contents, targetFolderId));
    });
  };
}

moveContents.request = (contents, folderId) => {
  return {
    type: FINDER_FOLDER_CONTENTS_MOVE,
    folderId,
    contents
  };
};

moveContents.receive = (results, folderId, folderContents) => {
  return {
    type: FINDER_FOLDER_CONTENTS_MOVE,
    folderId,
    folderContents,
    results
  };
};

moveContents.error = (error, contents, folderId) => {
  return {
    type: FINDER_FOLDER_CONTENTS_MOVE,
    folderId,
    contents,
    error
  };
};
/**
 * Move selected assets
 */


export function moveSelectedAssets(targetFolderId) {
  return (dispatch, getState) => {
    const state = getState();
    const selectedAssets = finderSelectors.getSelectedAssets(state);
    dispatch(moveContents(selectedAssets, targetFolderId));
  };
}
/**
 * PUT designmanager/v1/folders/:folderId
 */

export function renameFolder(folderId, folderName) {
  const {
    request,
    receive,
    error
  } = renameFolder;
  return (dispatch, getState) => {
    const state = getState();
    const folder = finderSelectors.getFolder(state, {
      folderId
    });
    const {
      name: currentFolderName
    } = folder;
    dispatch(request(folderId, folderName));
    return client.update(folderId, {
      name: folderName
    }).then(result => {
      dispatch(receive(folderId, folderName, result));
    }).catch(__error => {
      dispatch(error(folderId, folderName, currentFolderName, __error));
    });
  };
}

renameFolder.request = (folderId, folderName) => {
  return {
    type: FINDER_FOLDER_RENAME,
    folderId,
    folderName
  };
};

renameFolder.receive = (folderId, folderName, folder) => {
  return {
    type: FINDER_FOLDER_RENAME,
    folderId,
    folderName,
    folder
  };
};

renameFolder.error = (folderId, folderName, oldFolderName, error) => {
  return {
    type: FINDER_FOLDER_RENAME,
    folderId,
    folderName,
    oldFolderName,
    error
  };
};
/**
 * POST designmanager/v1/folders/:folderId/clone
 * @param {Number} folderId ID of folder to clone.
 */


export function duplicateFolder(folder, __parentFolderId) {
  const {
    request,
    receive,
    error
  } = duplicateFolder;
  const folderId = folder.id;
  const folderName = folder.name;
  const folderType = folder.type;
  return (dispatch, getState) => {
    const state = getState();
    const foldersById = finderSelectors.getFoldersById(state);

    const parentFolderId = __parentFolderId || finderSelectors.getRootFolderId(state);

    const isServerlessFunctionFolder = finderSelectors.getIsServerlessFunctionFolder(state, {
      folderId
    });
    let folderBaseName = folderName;

    if (isServerlessFunctionFolder) {
      const parts = folderName.split('.');
      parts.pop();
      folderBaseName = parts.join('.');
    }

    const copyBaseName = generateCopyName(folderBaseName, folderType, Object.values(foldersById || {}).filter(__folder => __folder.parentFolderId === parentFolderId));
    const copyName = isServerlessFunctionFolder ? `${copyBaseName}.${SERVERLESS_EXTENSION}` : copyBaseName;
    dispatch(request(folderId, copyName));
    client.clone(folderId, copyName).then(({
      folder: __folder,
      files,
      subfolders,
      folderMetadata
    }) => {
      dispatch(receive(__folder, files, subfolders, folderMetadata));
    }).catch(__error => {
      dispatch(error(__error, folderId, folderName));
    });
  };
}

duplicateFolder.request = (folderId, duplicateName) => {
  return {
    type: FINDER_FOLDER_DUPLICATE,
    folderId,
    duplicateName
  };
};

duplicateFolder.receive = (folder, files, subfolders, folderMetadata) => {
  return {
    type: FINDER_FOLDER_DUPLICATE,
    folder,
    files,
    subfolders,
    folderMetadata
  };
};

duplicateFolder.error = (error, folderId, folderName) => {
  return {
    type: FINDER_FOLDER_DUPLICATE,
    folderId,
    folderName,
    error
  };
};

export function copyPath(folderId) {
  const {
    success,
    fail
  } = copyPath;
  return (dispatch, getState) => {
    const state = getState();
    const folder = finderSelectors.getFoldersById(state)[folderId];

    if (!folder) {
      dispatch(fail());
      return;
    }

    copyToClipboard({
      text: convertToAbsolute(folder.fullPath),
      onSuccess: () => dispatch(success()),
      onFail: () => dispatch(fail())
    });
  };
}

copyPath.success = () => ({
  type: FINDER_FOLDER_COPY_PATH,
  copied: true
});

copyPath.fail = () => ({
  type: FINDER_FOLDER_COPY_PATH,
  copied: false
});
/**
 * Bulk delete folder contents
 */


export function bulkDeleteContents({
  files,
  subfolders
}) {
  return {
    type: FINDER_FOLDER_CONTENTS_BULK_DELETE,
    files,
    subfolders
  };
}
/**
 * Bulk delete folders
 * @param {Array.<Number>} folderIds
 *
 * DELETE designmanager/v1/folders/bulk
 */

export function bulkDeleteFolders(folderIds) {
  const {
    request,
    receive,
    error
  } = bulkDeleteFolders;
  return dispatch => {
    dispatch(request(folderIds));
    client.bulkDelete(folderIds).then(results => {
      dispatch(receive(results, folderIds));
    }).catch(__error => {
      dispatch(error(__error, folderIds));
    });
  };
}

bulkDeleteFolders.request = folderIds => {
  return {
    type: FINDER_FOLDERS_BULK_DELETE,
    folderIds
  };
};

bulkDeleteFolders.receive = (results, folderIds) => {
  return {
    type: FINDER_FOLDERS_BULK_DELETE,
    folderIds,
    results
  };
};

bulkDeleteFolders.error = (error, folderIds) => {
  return {
    type: FINDER_FOLDERS_BULK_DELETE,
    folderIds,
    error
  };
};
/**
 * PUT designmanager/v1/folder-metadata/folder/:folderId
 */


export function toggleLockFolder(folderId, lockMessage = 'default') {
  const {
    receive,
    error
  } = toggleLockFolder;
  return (dispatch, getState) => {
    const state = getState();
    const lockedStatus = getIsLocked(state, {
      folderId
    });

    const handleSuccess = newFolderMetadata => {
      dispatch(receive(folderId, newFolderMetadata));
    };

    const handleError = err => {
      dispatch(error(folderId, err));
    };

    if (lockedStatus) {
      return client.unlockFolder(folderId).then(handleSuccess, handleError);
    } else {
      return client.lockFolder(folderId, lockMessage).then(handleSuccess, handleError);
    }
  };
}

toggleLockFolder.receive = (folderId, newFolderMetadata) => {
  return {
    type: FINDER_FOLDER_TOGGLE_LOCK,
    folderId,
    newFolderMetadata
  };
};

toggleLockFolder.error = (folderId, error) => {
  return {
    type: FINDER_FOLDER_TOGGLE_LOCK,
    folderId,
    error
  };
};